import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { MediaType, YearRange } from "@/mocks/movies";
import { filterMedia } from "@/lib/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import NotFound from "@/pages/NotFound";
import FilterPanel from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const isValid = type === "movie" || type === "tv";
  const mediaType = (isValid ? type : "movie") as MediaType;

  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState<YearRange | null>(null);
  const [sort, setSort] = useState<"default" | "updated">("updated");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const isMovie = mediaType === "movie";
  const pageName = isMovie ? "电影" : "电视剧";

  useDocumentMeta(
    `${pageName} - 最近中文字幕`,
    `最近中文字幕为你整理最新${pageName}的中文字幕更新情况，包含剧情、类型、年份等筛选维度，方便快速找到想看的带中文字幕${pageName}。`,
    `中文字幕${pageName},${pageName}字幕,最新${pageName},中文字幕更新`
  );

  const results = useMemo(
    () => filterMedia(mediaType, { genre, yearRange: year, sort }),
    [mediaType, genre, year, sort]
  );

  const totalPages = Math.ceil(results.length / PAGE_SIZE) || 1;

  useEffect(() => {
    setPage(1);
  }, [genre, year, sort, mediaType]);

  useEffect(() => {
    setGenre("全部");
    setYear(null);
  }, [mediaType]);

  if (!isValid) {
    return <NotFound />;
  }

  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilter = genre !== "全部" || year !== null;

  const clearFilter = () => {
    setGenre("全部");
    setYear(null);
    setSort("default");
  };

  return (
    <div className="animate-fade-up">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{pageName}专区</span>
          </nav>
          <h1 className="mt-3 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-lg text-background-50 md:h-9 md:w-9">
              <i className={isMovie ? "ri-film-line" : "ri-tv-2-line"}></i>
            </span>
            {pageName}专区 - 最新中文字幕{pageName}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            汇总近期更新的带中文字幕{pageName}，支持按类型、年份筛选与排序，共收录 {results.length} 部作品。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        <button
          type="button"
          onClick={() => setShowFilters((prev) => !prev)}
          className="mb-4 flex w-full items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 md:hidden"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-500"></i>
            筛选与排序
            {hasFilter && (
              <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800">
                已筛选
              </span>
            )}
          </span>
          <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr] lg:gap-8">
          <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <div className="lg:sticky lg:top-24">
              <FilterPanel
                activeGenre={genre}
                activeYear={year}
                sort={sort}
                hasFilter={hasFilter}
                onGenreChange={setGenre}
                onYearChange={setYear}
                onSortChange={setSort}
                onClear={clearFilter}
              />
            </div>
          </aside>

          <div>
            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                  {pageItems.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
                <div className="mt-8">
                  <Pagination current={page} total={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                  <i className="ri-emotion-sad-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  没有找到匹配的{pageName}
                </h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  当前筛选条件下暂无结果，试试更换类型或年份，或清空筛选条件重新查找。
                </p>
                <button
                  type="button"
                  onClick={clearFilter}
                  className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line"></i>
                  清空筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}